/**
 * Pure planning helpers for the onboarding bins step. Kept separate from the React
 * component so the RE-ENTRY GUARD — the part most likely to break — is unit-testable
 * without rendering.
 *
 * The guard: a category that already has a bin must offer that bin as a link target
 * before proposing a new one. Bin names are NOT unique, so a naive re-run that always
 * creates "<Category> Feed" would silently make a SECOND poultry bin and split a
 * customer's flock across two bins that each deplete at half speed.
 */

export interface OnbAnimal {
  id: number;
  name: string;
  animal_type: string;
  category: string;
  animal_subtype?: string;
  life_stage?: string;
}

/**
 * THE RECOMMENDED FEED, CARRIED INTO THE BINS STEP (Brad, 2026-09-24: "when I set up my
 * home flock, it recommended layer feed. It should be showing that as the leading feed
 * on top, not having to scroll to find layer"). The animals step says "Recommended:
 * Layer Feed" (AnimalManager.getRecommendedFeed, by life stage); these are the same
 * calls as words the picker matches against a Red Bridge row's name, so that row leads
 * the list. A group of animals is the union, in the animals' order.
 */
export function recommendedFeedWords(animals: OnbAnimal[]): string[] {
  const out: string[] = [];
  const add = (w: string) => { if (!out.includes(w)) out.push(w); };
  for (const a of animals) {
    const stage = a.life_stage || '';
    const sub = a.animal_subtype || '';
    switch (a.category) {
      case 'poultry':
        if (stage === 'chick' || sub === 'chicks') add('starter');
        else if (stage === 'grower' || sub === 'pullets') add('grower');
        else if (stage === 'laying' || sub === 'laying_hens') add('layer');
        else if (stage === 'finishing' || sub === 'meat_birds') add('grower');
        else add('all flock');
        break;
      case 'goat': add(sub === 'dairy' ? 'dairy goat' : 'goat chow'); break;
      case 'sheep': add('sheep'); break;
      case 'swine': add('swine'); break;
      case 'horse': add('horse'); break;
      case 'rabbit': add('rabbit'); break;
      case 'cattle': add('all stock'); break;
      default: break;
    }
  }
  return out;
}

export interface OnbAnimalLink {
  id: number;
  animal_id: number;
  animal_name: string;
}

export interface OnbBin {
  id: number;
  name: string;
  feed_category: string;
  current_lbs: number;
  animal_links: OnbAnimalLink[];
}

export const CATEGORY_LABELS: Record<string, string> = {
  poultry: 'poultry', dog: 'dogs', cat: 'cats', goat: 'goats', sheep: 'sheep',
  swine: 'pigs', cattle: 'cattle', horse: 'horses', rabbit: 'rabbits', other: 'animals',
};

const BIN_NAMES: Record<string, string> = {
  poultry: 'Poultry Feed', dog: 'Dog Food', cat: 'Cat Food', goat: 'Goat Feed',
  sheep: 'Sheep Feed', swine: 'Pig Feed', cattle: 'Cattle Feed', horse: 'Horse Feed',
  rabbit: 'Rabbit Feed', other: 'Feed',
};

export function defaultBinName(category: string): string {
  return BIN_NAMES[category] || 'Feed';
}

/** IDs of every animal linked to ANY bin. */
export function linkedAnimalIds(bins: OnbBin[]): Set<number> {
  const s = new Set<number>();
  bins.forEach((b) => (b.animal_links || []).forEach((l) => s.add(l.animal_id)));
  return s;
}

/** Animals not yet linked to any bin — the only ones the step processes (re-entry safe). */
export function unlinkedAnimals(animals: OnbAnimal[], bins: OnbBin[]): OnbAnimal[] {
  const linked = linkedAnimalIds(bins);
  return animals.filter((a) => !linked.has(a.id));
}

export function groupByCategory(animals: OnbAnimal[]): Record<string, OnbAnimal[]> {
  const g: Record<string, OnbAnimal[]> = {};
  animals.forEach((a) => { (g[a.category] = g[a.category] || []).push(a); });
  return g;
}

/** Bins already in this category — the re-entry link targets. */
export function existingBinsForCategory(bins: OnbBin[], category: string): OnbBin[] {
  return bins.filter((b) => b.feed_category === category);
}

export type CategoryDefaultAction = 'link-existing' | 'create-new';

export interface CategoryPlan {
  category: string;
  animals: OnbAnimal[];       // the UNLINKED animals to place
  existingBins: OnbBin[];     // bins already in this category (offer these first)
  defaultAction: CategoryDefaultAction;
}

/**
 * THE RE-ENTRY GUARD. If a category already has a bin, the default action is to LINK the
 * new animals into it, NEVER to silently create a second same-category bin.
 */
export function planCategory(category: string, unlinked: OnbAnimal[], bins: OnbBin[]): CategoryPlan {
  const existingBins = existingBinsForCategory(bins, category);
  return {
    category,
    animals: unlinked,
    existingBins,
    defaultAction: existingBins.length > 0 ? 'link-existing' : 'create-new',
  };
}

/** One CategoryPlan per category that has unlinked animals. */
export function buildPlan(animals: OnbAnimal[], bins: OnbBin[]): CategoryPlan[] {
  const grouped = groupByCategory(unlinkedAnimals(animals, bins));
  return Object.keys(grouped).map((cat) => planCategory(cat, grouped[cat], bins));
}

/**
 * The completion invariant: every active animal is linked to a bin, and every bin has
 * BOTH a linked animal AND feed in it (current_lbs > 0) — so nobody finishes with an
 * unlinked animal or an empty bin that would immediately read as low.
 */
export function isStepComplete(animals: OnbAnimal[], bins: OnbBin[]): boolean {
  if (bins.length === 0) return false;
  if (unlinkedAnimals(animals, bins).length > 0) return false;
  return bins.every((b) => (b.animal_links?.length || 0) > 0 && Number(b.current_lbs) > 0);
}
